import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Badge,
  Button,
  useColorModeValue,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack as ChakraVStack,
  HStack as ChakraHStack,
  Radio,
  RadioGroup,
  Stack,
  Divider,
  useToast,
  Input,
} from '@chakra-ui/react'
import { 
  RepeatIcon, 
  ExternalLinkIcon, 
  InfoIcon, 
  WarningIcon
} from '@chakra-ui/icons'
import { PRM_Cilksan, PRM_CilkscaleBenchmark, PRM_Error } from '../types/unified_models'
import CodeEditor from './CodeEditor'
import { Chart, registerables } from 'chart.js'
import { useRef as useReactRef } from 'react'
Chart.register(...registerables)

interface ParallelAnalysisModuleProps {
  cilksan: PRM_Cilksan | PRM_Error | null
  cilkscaleBenchmark: PRM_CilkscaleBenchmark | PRM_Error | null
  sourceCode: string
}

export default function ParallelAnalysisModule({ 
  cilksan, 
  cilkscaleBenchmark, 
  sourceCode 
}: ParallelAnalysisModuleProps) {
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const toast = useToast()
  
  // Race detection modal
  const { isOpen: isRaceModalOpen, onOpen: onRaceModalOpen, onClose: onRaceModalClose } = useDisclosure()
  const [selectedRace, setSelectedRace] = useState<string>('all')
  const [annotatedCode, setAnnotatedCode] = useState<string>(sourceCode)
  const [raceSearchTerm, setRaceSearchTerm] = useState<string>('')
  
  // Scalability chart
  const chartRef = useRef<HTMLDivElement>(null)

  // Generate annotated code based on selected race
  useEffect(() => {
    if (!cilksan || 'error' in cilksan || !cilksan.distinct_race_list) {
      setAnnotatedCode(sourceCode)
      return
    }

    let code = sourceCode
    const lines = code.split('\n')
    
    // Clear previous annotations
    lines.forEach((line, index) => {
      if (line.includes('// RACE_ANNOTATION')) {
        lines[index] = line.replace(/\/\/ RACE_ANNOTATION.*$/, '')
      }
    })

    // Add annotations for selected race
    if (selectedRace === 'all') {
      // Add annotations for all races
      cilksan.distinct_race_list.forEach((race: any, raceIndex: number) => {
        race.memory_accesses?.forEach((access: any) => {
          const lineIndex = access.line_num - 1
          if (lineIndex >= 0 && lineIndex < lines.length) {
            const annotation = `// RACE_ANNOTATION Race ${raceIndex}: ${access.access_type}`
            lines[lineIndex] = lines[lineIndex] + ' ' + annotation
          }
        })
      })
    } else {
      // Add annotations for specific race
      const raceIndex = parseInt(selectedRace)
      if (raceIndex >= 0 && raceIndex < cilksan.distinct_race_list.length) {
        const race = cilksan.distinct_race_list[raceIndex]
        race?.memory_accesses?.forEach((access: any) => {
          const lineIndex = access.line_num - 1
          if (lineIndex >= 0 && lineIndex < lines.length) {
            const annotation = `// RACE_ANNOTATION Race ${raceIndex}: ${access.access_type}`
            lines[lineIndex] = lines[lineIndex] + ' ' + annotation
          }
        })
      }
    }

    setAnnotatedCode(lines.join('\n'))
  }, [cilksan, selectedRace, sourceCode])

  // Filter races based on search term
  const filteredRaces = useMemo(() => {
    if (!cilksan || 'error' in cilksan || !cilksan.distinct_race_list) return []
    
    if (!raceSearchTerm.trim()) return cilksan.distinct_race_list
    
    return cilksan.distinct_race_list.filter((race: any, index: number) => {
      const raceText = `Race ${index}`
      return raceText.toLowerCase().includes(raceSearchTerm.toLowerCase())
    })
  }, [cilksan, raceSearchTerm])

  // ScalabilityChart component
  function ScalabilityChart({ dataSeries }: { dataSeries: any }) {
    const chartContainer = useReactRef<HTMLCanvasElement>(null)
    const chartInstance = useRef<Chart | null>(null)

    useEffect(() => {
      if (!chartContainer.current) return
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
      const ctx = chartContainer.current.getContext('2d')
      if (!ctx) return

      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: dataSeries.num_workers,
          datasets: [
            {
              label: 'Observed Speedup',
              data: dataSeries.obs_speedup,
              borderColor: 'rgb(54, 162, 235)',
              backgroundColor: 'rgba(54, 162, 235, 0.2)',
              fill: false,
              tension: 0.1,
              pointRadius: 3,
            },
            {
              label: 'Perfect Linear Speedup',
              data: dataSeries.perf_lin_speedup,
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              borderDash: [5, 5],
              fill: false,
              tension: 0.1,
              pointRadius: 3,
            },
            {
              label: 'Burdened-dag Bound',
              data: dataSeries.greedy_speedup,
              borderColor: 'rgb(255, 205, 86)',
              backgroundColor: 'rgba(255, 205, 86, 0.2)',
              borderDash: [2, 2],
              fill: false,
              tension: 0.1,
              pointRadius: 3,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              display: true,
              position: 'top',
            },
            title: {
              display: true,
              text: 'Scalability Analysis',
            },
            tooltip: {
              mode: 'index',
              intersect: false,
            },
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false,
          },
          scales: {
            x: {
              type: 'linear',
              title: {
                display: true,
                text: '# Workers',
              },
              ticks: {
                precision: 0,
              },
            },
            y: {
              type: 'linear',
              title: {
                display: true,
                text: 'Speedup',
              },
              beginAtZero: true,
            },
          },
        },
      })
      return () => {
        chartInstance.current?.destroy()
      }
    }, [dataSeries])

    return <canvas ref={chartContainer} style={{ width: '100%', height: '300px' }} />
  }

  // Render race detection section
  const renderRaceDetection = () => {
    if (!cilksan) {
      return (
        <VStack align="stretch" spacing={3} w="100%">
          <HStack>
            <Icon as={RepeatIcon} color="gray.400" />
            <Heading size="sm" color={textColor}>Race Detection</Heading>
          </HStack>
          <Text fontSize="sm" color={textColor}>
            Race detection has not yet been run.
          </Text>
        </VStack>
      )
    }

    if ('error' in cilksan) {
      return (
        <VStack align="stretch" spacing={3} w="100%">
          <HStack>
            <Icon as={WarningIcon} color="red.400" />
            <Heading size="sm" color={textColor}>Race Detection</Heading>
          </HStack>
          <Text fontWeight="bold" color="red.500" fontSize="sm">
            Error running race detection
          </Text>
          {cilksan.details && (
            <Box>
              <Text fontWeight="medium" fontSize="sm" color={textColor}>Details:</Text>
              <Box
                as="pre"
                bg="gray.100"
                p={2}
                borderRadius="md"
                fontSize="xs"
                overflowX="auto"
                overflowY="auto"
                maxH="200px"
                maxW="100%"
              >
                {cilksan.details}
              </Box>
            </Box>
          )}
        </VStack>
      )
    }

    const raceCount = cilksan.distinct_races
    const isRaceFree = raceCount === 0

    return (
      <VStack align="stretch" spacing={3} w="100%">
        <HStack>
          <Icon as={RepeatIcon} color={isRaceFree ? "green.400" : "red.400"} />
          <Heading size="sm" color={textColor}>Race Detection</Heading>
        </HStack>
        
        <HStack justify="space-between">
          <Text fontSize="sm" color={textColor}>
            Races detected:
          </Text>
          <Badge 
            colorScheme={isRaceFree ? "green" : "red"} 
            fontSize="sm"
            px={2}
            py={1}
          >
            {raceCount}
          </Badge>
        </HStack>

        {cilksan.distinct_race_list && cilksan.distinct_race_list.length > 0 && (
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<ExternalLinkIcon />}
              onClick={onRaceModalOpen}
            >
              View Annotated Code
            </Button>
          </HStack>
        )}

        {/* Race Detection Modal */}
        <Modal isOpen={isRaceModalOpen} onClose={onRaceModalClose} size="6xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader color={textColor}>
              <HStack>
                <Icon as={RepeatIcon} color="blue.400" />
                <Text>Cilksan Race Report (Annotated Source Code)</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <HStack spacing={4} align="start" h="500px">
                {/* Left: Race Selection */}
                <VStack spacing={3} align="stretch" w="250px" flexShrink={0}>
                  <Text fontWeight="medium" fontSize="sm" color={textColor}>
                    View specific races:
                  </Text>
                  
                  {/* Search input */}
                  <Input
                    size="sm"
                    placeholder="Search races..."
                    value={raceSearchTerm}
                    onChange={(e) => setRaceSearchTerm(e.target.value)}
                    bg="white"
                  />
                  
                  {/* Race list with scroll */}
                  <Box 
                    border="1px" 
                    borderColor="gray.200" 
                    borderRadius="md" 
                    bg="gray.50"
                    maxH="300px"
                    overflowY="auto"
                    p={2}
                  >
                    <RadioGroup value={selectedRace} onChange={setSelectedRace}>
                      <Stack direction="column" spacing={2}>
                        <Radio value="all" colorScheme="red" size="sm">
                          All Races
                        </Radio>
                        {filteredRaces.map((race: any, filteredIndex: number) => {
                          // Find the original index of this race
                          const originalIndex = cilksan.distinct_race_list?.findIndex((r: any) => r === race) ?? filteredIndex
                          return (
                            <Radio 
                              key={originalIndex} 
                              value={originalIndex.toString()} 
                              colorScheme="red" 
                              size="sm"
                            >
                              Race {originalIndex}
                            </Radio>
                          )
                        })}
                        {filteredRaces.length === 0 && (
                          <Text fontSize="xs" color="gray.500" textAlign="center">
                            No races found
                          </Text>
                        )}
                      </Stack>
                    </RadioGroup>
                  </Box>
                  
                  {/* Race count info */}
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    {filteredRaces.length} of {cilksan.distinct_race_list?.length || 0} races
                  </Text>
                </VStack>

                {/* Right: Annotated Code */}
                <VStack spacing={3} align="stretch" flex={1} h="100%">
                  <Text fontWeight="medium" fontSize="sm" color={textColor}>
                    Annotated Source Code:
                  </Text>
                  <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" flex={1}>
                    <CodeEditor
                      value={annotatedCode}
                      onChange={() => {}} // Read-only
                      language="cpp"
                      height="100%"
                    />
                  </Box>
                </VStack>
              </HStack>

              {/* Full Report (below the side-by-side layout) */}
              <Box mt={4}>
                <Text fontWeight="medium" fontSize="sm" color={textColor} mb={2}>
                  Cilksan Full Report:
                </Text>
                <Box
                  as="pre"
                  bg="gray.100"
                  p={3}
                  borderRadius="md"
                  fontSize="xs"
                  overflowX="auto"
                  maxH="200px"
                >
                  {cilksan.full_report}
                </Box>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    )
  }

  // Render scalability analysis section
  const renderScalabilityAnalysis = () => {
    if (!cilkscaleBenchmark) {
      return (
        <VStack align="stretch" spacing={3} w="100%">
          <HStack>
            <Icon as={RepeatIcon} color="gray.400" />
            <Heading size="sm" color={textColor}>Scalability Analysis</Heading>
          </HStack>
          <Text fontSize="sm" color={textColor}>
            Scalability analysis has not yet been run.
          </Text>
        </VStack>
      )
    }

    if ('error' in cilkscaleBenchmark) {
      return (
        <VStack align="stretch" spacing={3} w="100%">
          <HStack>
            <Icon as={WarningIcon} color="red.400" />
            <Heading size="sm" color={textColor}>Scalability Analysis</Heading>
          </HStack>
          <Text fontWeight="bold" color="red.500" fontSize="sm">
            Error running scalability analysis
          </Text>
        </VStack>
      )
    }

    // Get the first data series for display
    const dataSeries = cilkscaleBenchmark.data[0]
    if (!dataSeries) {
      return (
        <VStack align="stretch" spacing={3} w="100%">
          <HStack>
            <Icon as={RepeatIcon} color="yellow.400" />
            <Heading size="sm" color={textColor}>Scalability Analysis</Heading>
          </HStack>
          <Text fontSize="sm" color={textColor}>
            No scalability data available.
          </Text>
        </VStack>
      )
    }

    const measuredParallelism = dataSeries.span_speedup[0]

    return (
      <VStack align="stretch" spacing={3} w="100%">
        <HStack>
          <Icon as={RepeatIcon} color="blue.400" />
          <Heading size="sm" color={textColor}>Scalability Analysis</Heading>
        </HStack>
        
        <HStack justify="space-between">
          <Text fontSize="sm" color={textColor}>
            Measured parallelism:
          </Text>
          <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
            {measuredParallelism?.toFixed(2) ?? 'N/A'}
          </Badge>
        </HStack>

        {/* Scalability Chart */}
        <Box>
          <Text fontWeight="medium" fontSize="sm" color={textColor} mb={2}>
            Scalability Chart:
          </Text>
          <Box border="1px" borderColor="gray.200" borderRadius="md" bg="gray.50" p={2}>
            <ScalabilityChart dataSeries={dataSeries} />
          </Box>
        </Box>

        {/* Data Summary */}
        <Box>
          <Text fontWeight="medium" fontSize="sm" color={textColor} mb={2}>
            Data Summary:
          </Text>
          <VStack spacing={1} align="stretch" fontSize="xs">
            <HStack justify="space-between">
              <Text color={textColor}>Number of workers tested:</Text>
              <Text color={textColor}>{dataSeries.num_workers.length}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color={textColor}>Max workers:</Text>
              <Text color={textColor}>{Math.max(...dataSeries.num_workers)}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color={textColor}>Max observed speedup:</Text>
              <Text color={textColor}>{Math.max(...dataSeries.obs_speedup).toFixed(2)}x</Text>
            </HStack>
          </VStack>
        </Box>
      </VStack>
    )
  }

  return (
    <VStack spacing={6} align="stretch" w="100%" maxW="100%" minW={0} overflow="hidden">
      {/* Race Detection */}
      <Box
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        p={4}
        shadow="sm"
      >
        {renderRaceDetection()}
      </Box>

      {/* Scalability Analysis */}
      <Box
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        p={4}
        shadow="sm"
      >
        {renderScalabilityAnalysis()}
      </Box>
    </VStack>
  )
} 